import React, { useState } from 'react';
import { StorageFolder, DanceState, LibraryClipboard } from '../../types';
import { useDanceStore } from '../../store/useDanceStore';
import { useStorageStore, UNSORTED_POSES_ID, UNSORTED_SEQUENCES_ID, SHARED_SEQUENCES_ID } from '../../store/useStorageStore';
import { FolderPlus, FilePlus, Clipboard, Folder as FolderIcon, FolderOpen, Edit2, Trash2, ChevronRight, ChevronDown, Home, Lock, Users, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import { trackShareEvent } from '../../lib/analytics';



interface FolderTreeProps {
  folders: StorageFolder[];
  selectedFolderId: string | null;
  onSelectFolder: (id: string | null) => void;
  onCreateFolder: (parentId: string | null) => void;
  onRenameFolder: (id: string, newName: string) => void;
  onDeleteFolder: (id: string) => void;
  onMoveItem?: (id: string, targetFolderId: string | null) => void;
  title?: string;
}

export const FolderTree: React.FC<FolderTreeProps> = ({
  folders, selectedFolderId, onSelectFolder, onCreateFolder, onRenameFolder, onDeleteFolder, onMoveItem, title
}) => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const displayTitle = title || t('folderTree.library');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [draggedFolderId, setDraggedFolderId] = useState<string | null>(null);

  const isDescendantOf = (parent: string | null, potentialAncestor: string): boolean => {
    let curr = parent;
    while (curr) {
      if (curr === potentialAncestor) return true;
      const f = folders.find(x => x.id === curr);
      curr = f ? f.parentId : null;
    }
    return false;
  };
  const openInputDialog = useDanceStore((state: DanceState) => state.openInputDialog);
  const confirmAction = useDanceStore((state: DanceState) => state.confirmAction);
  const clipboard = useStorageStore(state => state.clipboard);
  const setClipboard = useStorageStore(state => state.setClipboard);
  const multiMovePoses = useStorageStore(state => state.multiMovePoses);
  const multiMoveSequences = useStorageStore(state => state.multiMoveSequences);
  const duplicatePose = useStorageStore(state => state.duplicatePose);
  const duplicateSequence = useStorageStore(state => state.duplicateSequence);
  const moveFolder = useStorageStore(state => state.moveFolder);
  const duplicateFolder = useStorageStore(state => state.duplicateFolder);
  const openContextMenu = useDanceStore(state => state.openContextMenu);

  // Auto-expand parents of selected folder
  React.useEffect(() => {
    if (!selectedFolderId) return;
    
    const newExpanded = { ...expanded };
    let currentId = selectedFolderId;
    let found = false;

    // Find the folder and its ancestors
    while (currentId) {
      const folder = folders.find(f => f.id === currentId);
      if (folder && folder.parentId) {
        if (!newExpanded[folder.parentId]) {
          newExpanded[folder.parentId] = true;
          found = true;
        }
        currentId = folder.parentId;
      } else {
        break;
      }
    }

    if (found) {
      setExpanded(newExpanded);
    }
  }, [selectedFolderId, folders]);

  React.useEffect(() => {
    if (isMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.key) return;
      // 1. Only handle if this explorer matches the active category
      const activeModule = useDanceStore.getState().activeModule;
      const expectedCategory = activeModule === 'Sequences' ? 'sequences' : activeModule === 'Poses' ? 'poses' : null;
      if (!expectedCategory || folders.some(f => f.category !== expectedCategory)) return;

      // 2. Only handle if no pose/sequence cards are selected (giving cards priority)
      if (useDanceStore.getState().librarySelection.length > 0) return;
      if (document.activeElement?.tagName === 'INPUT') return;

      // Ensure a folder is selected and is not a system default for write actions
      const currFolder = folders.find(f => f.id === selectedFolderId);

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (currFolder && !currFolder.isSystem) {
          e.preventDefault();
          confirmAction({
            title: t('folderTree.delete_folder'),
            message: t('folderTree.delete_confirm', { name: currFolder.name }),
            onConfirm: () => onDeleteFolder(currFolder.id)
          });
        }
      } else if (e.key === 'c' && (e.ctrlKey || e.metaKey)) {
        if (currFolder) {
          e.preventDefault();
          setClipboard({ type: 'copy', itemType: 'folder', itemIds: [currFolder.id] });
          useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: 1 }));
        }
      } else if (e.key === 'x' && (e.ctrlKey || e.metaKey)) {
        if (currFolder) {
          e.preventDefault();
          setClipboard({ type: 'cut', itemType: 'folder', itemIds: [currFolder.id] });
          useDanceStore.getState().setToastMessage(t('library.cut_to_clipboard', { count: 1 }));
        }
      } else if (e.key === 'F2') {
        if (currFolder && !currFolder.isSystem) {
          e.preventDefault();
          openInputDialog({
            title: t('folderTree.rename_folder'),
            defaultName: currFolder.name,
            placeholder: t('folderTree.new_folder_name'),
            submitLabel: t('folderTree.rename'),
            onSave: (newName: string) => onRenameFolder(currFolder.id, newName)
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFolderId, clipboard, folders, isMobile]);



  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderFolder = (folder: StorageFolder, depth = 0) => {
    const children = folders.filter(f => f.parentId === folder.id);
    const isExpanded = expanded[folder.id] || false;
    const isSelected = selectedFolderId === folder.id;
    const isDragOver = dragOverFolderId === folder.id;
    const isSystem = !!folder.isSystem;

    return (
      <div key={folder.id}>
        <div
          id={`library-folder-${folder.id}`}
          onClick={() => {
            onSelectFolder(folder.id);
            if (folder.id === SHARED_SEQUENCES_ID) {
              trackShareEvent('folder_refreshed', { trigger: 'folder_click' });
              useStorageStore.getState().loadFromServer();
            }
          }}
          draggable={!isMobile && !isSystem}
          onDragStart={(e) => {
            if (isMobile || isSystem) return;
            e.stopPropagation();
            e.dataTransfer.setData('sourceId', folder.id);
            e.dataTransfer.setData('sourceType', 'folder');
            e.dataTransfer.effectAllowed = 'copyMove';
            setDraggedFolderId(folder.id);
          }}
          onDragEnd={() => setDraggedFolderId(null)}
          onDragOver={(e) => {
            if (isMobile) return;
            e.preventDefault();
            e.stopPropagation();

            const isFromShared = e.dataTransfer.types.includes('choreographer/from-shared');
            const isToShared = folder.id === 'shared-sequences';
            if (isFromShared || isToShared) {
              e.dataTransfer.dropEffect = 'none';
              setDragOverFolderId(null);
              return;
            }

            // Invalid target: dragging a folder onto itself or one of its
            // own descendants. Show the invalid-operation cursor instead of
            // a valid move/copy cursor.
            if (draggedFolderId && (draggedFolderId === folder.id || isDescendantOf(folder.id, draggedFolderId))) {
              e.dataTransfer.dropEffect = 'none';
              setDragOverFolderId(null);
              return;
            }

            setDragOverFolderId(folder.id);
            if (e.ctrlKey || e.metaKey) {
              e.dataTransfer.dropEffect = 'copy';
            } else {
              e.dataTransfer.dropEffect = 'move';
            }
          }}
          onDragLeave={() => setDragOverFolderId(null)}
          onDrop={(e) => {
            if (isMobile) return;
            e.preventDefault();
            e.stopPropagation();
            setDragOverFolderId(null);

            const isFromShared = e.dataTransfer.types.includes('choreographer/from-shared') || e.dataTransfer.getData('sourceFolderId') === 'shared-sequences';
            const isToShared = folder.id === 'shared-sequences';
            if (isFromShared || isToShared) {
              const toastMsg = isToShared 
                ? t('sharing.drag_in_blocked', 'Cannot move items into the Shared folder.')
                : t('sharing.drag_out_blocked', 'Cannot move items out of the Shared folder.');
              useDanceStore.getState().setToastMessage(toastMsg, 'error');
              return;
            }

            const sourceIdsStr = e.dataTransfer.getData('sourceId');
            const sourceType = e.dataTransfer.getData('sourceType');
            if (sourceIdsStr && sourceType) {
              const ids = sourceIdsStr.split(',');
              const isCopy = e.ctrlKey || e.metaKey;

              if (sourceType === 'folder') {
                const sourceFolderId = sourceIdsStr;
                if (sourceFolderId === folder.id) return;

                const isDescendant = (parent: string | null, potentialAncestor: string): boolean => {
                  let curr = parent;
                  while (curr) {
                    if (curr === potentialAncestor) return true;
                    const f = folders.find(x => x.id === curr);
                    curr = f ? f.parentId : null;
                  }
                  return false;
                };

                if (isDescendant(folder.id, sourceFolderId)) {
                  useDanceStore.getState().setToastMessage(t('folderTree.cannot_move_inside_descendant', { defaultValue: "Cannot move a folder inside its own subfolder!" }), 'error');
                  return;
                }

                if (isCopy) {
                  duplicateFolder(sourceFolderId, folder.id);
                  useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: 1 }));
                } else {
                  moveFolder(sourceFolderId, folder.id);
                  useDanceStore.getState().setToastMessage(t('folderTree.moved_items', { count: 1, type: t('nav.folder', { defaultValue: 'folder' }) }));
                }
              } else {
                if (isCopy) {
                  ids.forEach(id => {
                    if (sourceType === 'pose') {
                      duplicatePose(id, folder.id);
                    } else if (sourceType === 'sequence') {
                      duplicateSequence(id, folder.id);
                    }
                  });
                  useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: ids.length }));
                } else {
                  if (sourceType === 'pose') {
                    multiMovePoses(ids, folder.id);
                  } else if (sourceType === 'sequence') {
                    multiMoveSequences(ids, folder.id);
                  }
                  useDanceStore.getState().setToastMessage(t('folderTree.moved_items', { count: ids.length, type: sourceType + (ids.length > 1 ? 's' : '') }));
                }
              }
            }
          }}
          onContextMenu={(e) => {
            if (isMobile) return;
            e.preventDefault();
            e.stopPropagation();

            const category = folder.category;
            const canPaste = clipboard && clipboard.itemType === (category === 'sequences' ? 'sequence' : 'pose') && folder.id !== SHARED_SEQUENCES_ID;

            const menuItems: any[] = [
              ...(!isSystem ? [
                {
                  label: t('folderTree.new_subfolder'),
                  icon: FolderPlus,
                  action: () => onCreateFolder(folder.id)
                },
                { isDivider: true },
                {
                  label: t('folderTree.rename_folder'),
                  icon: Edit2,
                  shortcut: 'F2',
                  action: () => {
                    openInputDialog({
                      title: t('folderTree.rename_folder'),
                      defaultName: folder.name,
                      onSave: (newName: string) => onRenameFolder(folder.id, newName)
                    });
                  }
                }
              ] : []),
              ...(!isSystem && folder.parentId !== null ? [
                {
                  label: t('folderTree.move_to_root'),
                  icon: Home,
                  action: () => {
                    moveFolder(folder.id, null);
                    useDanceStore.getState().setToastMessage(t('folderTree.moved_items', { count: 1, type: t('nav.folder', { defaultValue: 'folder' }) }));
                  }
                },
                { isDivider: true }
              ] : []),
              {
                label: t('folderTree.paste_into_folder'),
                icon: Clipboard,
                disabled: !canPaste,
                action: () => {
                   if (!clipboard) return;

                   // Name collisions in the target folder are auto-disambiguated
                   // by multiMoveSequences/moveSequence/duplicateSequence
                   // themselves — never blocked here.
                   if (clipboard.type === 'cut') {
                     if (clipboard.itemType === 'pose') multiMovePoses(clipboard.itemIds, folder.id);
                     else multiMoveSequences(clipboard.itemIds, folder.id);
                     setClipboard(null);
                   } else {
                     clipboard.itemIds.forEach(id => {
                       if (clipboard.itemType === 'pose') {
                         duplicatePose(id, folder.id);
                       } else {
                         duplicateSequence(id, folder.id);
                       }
                     });
                   }
                   useDanceStore.getState().setToastMessage(t('folderTree.pasted_items', { count: clipboard.itemIds.length }));
                }
              },
              ...(!isSystem ? [
                { isDivider: true },
                {
                  label: t('folderTree.delete_folder'),
                  icon: Trash2,
                  className: 'danger',
                  shortcut: 'Del',
                  action: () => {
                    confirmAction({
                      title: t('folderTree.delete_folder'),
                      message: t('folderTree.delete_confirm', { name: folder.name }),
                      onConfirm: () => onDeleteFolder(folder.id)
                    });
                  }
                }
              ] : [])
            ];

            openContextMenu(e.clientX, e.clientY, menuItems);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            padding: `4px 8px 4px ${8 + depth * 16}px`,
            cursor: 'pointer',
            background: isSelected ? 'rgba(56, 189, 248, 0.12)' : (isDragOver ? 'rgba(56, 189, 248, 0.2)' : 'transparent'),
            color: (isSelected || isDragOver) ? '#38bdf8' : 'rgba(255,255,255,0.85)',
            borderLeft: (isSelected || isDragOver) ? '2px solid #38bdf8' : '2px solid transparent',
            outline: 'none',
            userSelect: 'none',
            transition: 'all 0.15s ease',
            boxShadow: isSelected ? 'inset 4px 0 15px -5px rgba(56, 189, 248, 0.1)' : 'none'
          }}
          className="folder-item hover-bg"
        >
          <div 
            style={{ 
              width: 16, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              marginRight: 4,
              cursor: children.length > 0 ? 'pointer' : 'default',
              color: 'rgba(255,255,255,0.4)'
            }} 
            onClick={(e) => {
              if (children.length > 0) toggleExpand(folder.id, e);
            }}
          >
            {children.length > 0 && (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
          </div>
          <div style={{ marginRight: 8, display: 'flex', alignItems: 'center' }}>
            {folder.id === SHARED_SEQUENCES_ID ? (
              <Users size={16} style={{ color: isSelected ? '#38bdf8' : 'rgba(255,255,255,0.7)' }} />
            ) : isSelected ? (
              <FolderOpen size={16} />
            ) : (
              <FolderIcon size={16} opacity={children.length > 0 ? 1 : 0.6} />
            )}
          </div>
          <span className="folder-name" style={{ flex: 1, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={folder.name}>{folder.name}</span>
          {isSystem && <Lock size={11} style={{ opacity: 0.35, flexShrink: 0, marginLeft: 4 }} />}
          {folder.id === SHARED_SEQUENCES_ID && (
            <button
              title={t('sharing.refresh_shared', 'Refresh shared items')}
              onClick={(e) => {
                e.stopPropagation();
                onSelectFolder(folder.id);
                trackShareEvent('folder_refreshed', { trigger: 'button_click' });
                useStorageStore.getState().loadFromServer();
              }}
              className="refresh-shared-btn"
            >
              <RefreshCw size={11} />
            </button>
          )}
        </div>
        
        {isExpanded && children.length > 0 && (
          <div>
            {children.map(c => renderFolder(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const rootFolders = folders
    .filter(f => f.parentId === null)
    .sort((a, b) => (b.isSystem ? 1 : 0) - (a.isSystem ? 1 : 0));

  if (isMobile) {
    // Root view: show full folder list
    if (selectedFolderId === null) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflowY: 'auto', padding: '12px 0' }}>
          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 12 }} />
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {rootFolders.map(f => renderFolder(f))}
          </div>
        </div>
      );
    }

    // Subfolder view: breadcrumb navigation
    const currentFolder = folders.find(f => f.id === selectedFolderId);
    if (!currentFolder) return null;

    const hasChildren = folders.some(f => f.parentId === currentFolder.id);
    const isDirectChild = currentFolder.parentId === null;

    const handleChildDotClick = () => {
      const newExpanded: Record<string, boolean> = {};
      let currentId: string | null = currentFolder.id;
      newExpanded[currentId] = true;
      while (currentId) {
        const f = folders.find(x => x.id === currentId);
        if (f && f.parentId) {
          newExpanded[f.parentId] = true;
          currentId = f.parentId;
        } else {
          break;
        }
      }
      setExpanded(newExpanded);
      onSelectFolder(null);
    };

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 2px',
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        gap: '2px'
      }}>
        {/* Home button — navigates to root */}
        <button
          onClick={() => onSelectFolder(null)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.5)',
            padding: '4px 2px',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            verticalAlign: 'middle'
          }}
        >
          <Home size={14} />
        </button>

        <span style={{ color: 'rgba(255,255,255,0.25)', margin: '0 2px', fontSize: '0.8rem' }}>/</span>

        {/* Parent ".." if not a direct child of root */}
        {!isDirectChild && currentFolder.parentId && (
          <>
            <button
              onClick={() => onSelectFolder(currentFolder.parentId!)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.85rem',
                fontWeight: 500,
                padding: '4px 2px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                verticalAlign: 'middle'
              }}
            >
              ..
            </button>
            <span style={{ color: 'rgba(255,255,255,0.25)', margin: '0 2px', fontSize: '0.8rem' }}>/</span>
          </>
        )}

        {/* Current folder name */}
        <span
          style={{
            fontSize: '0.85rem',
            fontWeight: 700,
            padding: '4px 2px',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            verticalAlign: 'middle'
          }}
          className="folder-name"
        >
          {currentFolder.name}
        </span>

        {/* Child ".." if the folder has children */}
        {hasChildren && (
          <>
            <span style={{ color: 'rgba(255,255,255,0.25)', margin: '0 2px', fontSize: '0.8rem' }}>/</span>
            <button
              onClick={handleChildDotClick}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.5)',
                fontSize: '0.85rem',
                fontWeight: 500,
                padding: '4px 2px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                verticalAlign: 'middle'
              }}
            >
              ..
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div
        onDragOver={(e) => {
          if (isMobile) return;
          const isFromShared = e.dataTransfer.types.includes('choreographer/from-shared');
          if (isFromShared) {
            e.dataTransfer.dropEffect = 'none';
            return;
          }
          const hasSource = e.dataTransfer.types.some(t => t.toLowerCase() === 'sourcetype');
          if (hasSource) {
            e.preventDefault();
            if (e.ctrlKey || e.metaKey) {
              e.dataTransfer.dropEffect = 'copy';
            } else {
              e.dataTransfer.dropEffect = 'move';
            }
          }
        }}
        onDrop={(e) => {
          if (isMobile) return;
          e.preventDefault();
          const isFromShared = e.dataTransfer.types.includes('choreographer/from-shared') || e.dataTransfer.getData('sourceFolderId') === 'shared-sequences';
          if (isFromShared) {
            useDanceStore.getState().setToastMessage(t('sharing.drag_out_blocked', 'Cannot move items out of the Shared folder.'), 'error');
            return;
          }
          const sourceIdsStr = e.dataTransfer.getData('sourceId');
          const sourceType = e.dataTransfer.getData('sourceType');
          if (sourceIdsStr && sourceType) {
            const isCopy = e.ctrlKey || e.metaKey;
            const category = folders[0]?.category;
            const unsortedId = category === 'poses' ? UNSORTED_POSES_ID : UNSORTED_SEQUENCES_ID;

            if (sourceType === 'folder') {
              const sourceFolderId = sourceIdsStr;
              const currFolder = folders.find(f => f.id === sourceFolderId);
              if (!currFolder || currFolder.parentId === null || currFolder.isSystem) return;

              if (isCopy) {
                duplicateFolder(sourceFolderId, null);
                useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: 1 }));
              } else {
                moveFolder(sourceFolderId, null);
                useDanceStore.getState().setToastMessage(t('folderTree.moved_items', { count: 1, type: t('nav.folder', { defaultValue: 'folder' }) }));
              }
            } else {
              const ids = sourceIdsStr.split(',');
              if (isCopy) {
                ids.forEach(id => {
                  if (sourceType === 'pose') {
                    duplicatePose(id, unsortedId);
                  } else if (sourceType === 'sequence') {
                    duplicateSequence(id, unsortedId);
                  }
                });
                useDanceStore.getState().setToastMessage(t('library.copied_to_clipboard', { count: ids.length }));
              } else {
                if (sourceType === 'pose') {
                  multiMovePoses(ids, unsortedId);
                } else if (sourceType === 'sequence') {
                  multiMoveSequences(ids, unsortedId);
                }
                useDanceStore.getState().setToastMessage(t('folderTree.moved_items', { count: ids.length, type: sourceType + (ids.length > 1 ? 's' : '') }));
              }
            }
          }
        }}
        style={{ minHeight: '100%' }}
      >
        {rootFolders.map(f => renderFolder(f))}
      </div>
  );
};
